import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { ActiveCourse, ActiveCoursesFilter } from '../../../types/activeCourses.types';
import { apiService } from '../../../services/api';
import CourseFilters from '../components/CourseFilters';
import CourseCard from '../components/CourseCard';

const ActiveClasses: React.FC = () => {
  const [activeCourses, setActiveCourses] = useState<ActiveCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<string>('all');

  const loadActiveCourses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filter: ActiveCoursesFilter = {
        searchTerm: searchTerm || undefined
      };

      // Use the internal api instance directly for now
      const response = await (apiService as any).api.get('/activecourses', { params: filter });
      setActiveCourses(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load active courses');
      console.error('Error loading active courses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActiveCourses();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadActiveCourses();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleJoinClass = (joinUrl: string) => {
    window.open(joinUrl, '_blank');
  };

  const handleCopyGuid = async (courseGuid: string) => {
    try {
      await navigator.clipboard.writeText(courseGuid);
      // You could add a toast notification here if desired
    } catch (err) {
      console.error('Failed to copy course GUID:', err);
    }
  };

  const filteredCourses = activeCourses.filter(course => 
    selectedVersion === 'all' || course.courseVersion === selectedVersion
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Filters */}
      <CourseFilters
        searchTerm={searchTerm}
        selectedVersion={selectedVersion}
        courseCount={filteredCourses.length}
        onSearchChange={setSearchTerm}
        onVersionChange={setSelectedVersion}
      />

      {/* Loading and Error States */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Course Cards with Responsive Grid */}
      {!loading && !error && (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',                                    // Mobile: 1 column
            sm: 'repeat(auto-fit, minmax(350px, 1fr))',   // Small: 2 columns if space allows
            md: 'repeat(auto-fit, minmax(380px, 1fr))',   // Medium: 2-3 columns
            lg: 'repeat(auto-fit, minmax(400px, 1fr))',   // Large: 3 columns preferred
            xl: 'repeat(auto-fit, minmax(450px, 1fr))'    // XL: 3+ columns with more space
          },
          gap: 3
        }}>
          {filteredCourses.map((course) => (
            <CourseCard
              key={course.courseGuid}
              course={course}
              onJoinClass={handleJoinClass}
              onCopyGuid={handleCopyGuid}
            />
          ))}
          
          {filteredCourses.length === 0 && !loading && (
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No active courses found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search filters or check back later for new courses.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ActiveClasses;
